import { useEffect, useState, CSSProperties } from 'react';
import axios from 'axios';
import { ZoomBtn } from './zoom/ZoomTable';
import { orgName } from './customerCtx';

// Desk-phone speed dials (2026-11-04): organisation-wide list + per-user extras,
// pushed to Poly desk phones as their (locked) contacts list.
type Item = { label: string; number: string };
type SdUser = { id: number; username: string; first_name?: string; last_name?: string; extension_number?: string; count: number; has_phone: boolean };

const inp: CSSProperties = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const uName = (u: SdUser) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;

function ListEditor({ items, onChange, max }: { items: Item[]; onChange: (v: Item[]) => void; max: number }) {
  const set = (i: number, k: keyof Item, v: string) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const n = items.slice(); [n[i], n[j]] = [n[j], n[i]]; onChange(n);
  };
  return (
    <div>
      {items.length === 0 && <div style={{ fontSize: 13, color: '#6b7280', padding: '6px 0' }}>No speed dials yet.</div>}
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <span style={{ width: 22, textAlign: 'right', color: '#9ca3af', fontSize: 12 }}>{i + 1}</span>
          <input style={{ ...inp, width: 220 }} placeholder="Name shown on the phone" maxLength={40} value={it.label} onChange={(e) => set(i, 'label', e.target.value)} />
          <input style={{ ...inp, width: 180 }} placeholder="Number or extension" value={it.number} onChange={(e) => set(i, 'number', e.target.value)} />
          <ZoomBtn onClick={() => move(i, -1)}>↑</ZoomBtn>
          <ZoomBtn onClick={() => move(i, 1)}>↓</ZoomBtn>
          <ZoomBtn onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</ZoomBtn>
        </div>
      ))}
      <div style={{ marginTop: 6 }}>
        <ZoomBtn onClick={() => { if (items.length < max) onChange([...items, { label: '', number: '' }]); }}>+ Add speed dial</ZoomBtn>
      </div>
    </div>
  );
}

export default function SpeedDialsPage() {
  const [tab, setTab] = useState<'org' | 'user'>('org');
  const [max, setMax] = useState(20);
  const [orgItems, setOrgItems] = useState<Item[] | null>(null);
  const [users, setUsers] = useState<SdUser[]>([]);
  const [search, setSearch] = useState('');
  const [selUser, setSelUser] = useState<SdUser | null>(null);
  const [userItems, setUserItems] = useState<Item[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => axios.get('/api/speed-dials')
    .then((r) => { setOrgItems(r.data.items || []); setUsers(r.data.users || []); setMax(r.data.max || 20); })
    .catch((e) => setMsg(err(e)));
  useEffect(() => { load(); }, []);

  const pickUser = (u: SdUser) => {
    setSelUser(u); setMsg('');
    axios.get(`/api/speed-dials/users/${u.id}`).then((r) => setUserItems(r.data.items || [])).catch((e) => setMsg(err(e)));
  };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      if (tab === 'org') await axios.put('/api/speed-dials', { items: orgItems });
      else if (selUser) await axios.put('/api/speed-dials', { user_id: selUser.id, items: userItems });
      setMsg('Saved.'); load();
    } catch (e) { setMsg(err(e)); }
    setBusy(false);
  };
  const discard = () => { setMsg(''); load(); if (selUser) pickUser(selUser); };

  if (!orgItems) return <div style={{ padding: 20, color: '#6b7280' }}>{msg || 'Loading…'}</div>;
  const tabBtn = (id: 'org' | 'user', label: string) => (
    <button onClick={() => { setTab(id); setMsg(''); }}
      style={{ padding: '8px 14px', border: 'none', borderBottom: tab === id ? '2px solid #0b5cff' : '2px solid transparent', background: 'none', fontWeight: tab === id ? 600 : 400, color: tab === id ? '#0b5cff' : '#374151', cursor: 'pointer' }}>{label}</button>
  );
  const room = Math.max(0, max - orgItems.length);

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <p style={{ fontSize: 13, color: '#4b5563', marginTop: 0 }}>
        Speed dials appear as favourites on desk phones (Poly). Each phone shows the organisation list first, then its user's own — at most {max} in total.
        The contacts list on the phone is locked and managed here only. Phones pick up changes overnight (03:00–04:00) or when restarted.
      </p>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 14 }}>
        {tabBtn('org', `Everyone in ${orgName()}`)}
        {tabBtn('user', 'Per user')}
      </div>

      {tab === 'org' && <ListEditor items={orgItems} onChange={setOrgItems} max={max} />}

      {tab === 'user' && (
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
          <div style={{ width: 300 }}>
            <input style={{ ...inp, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} placeholder="Search users" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              {users.filter((u) => !search || `${u.username} ${uName(u)} ${u.extension_number || ''}`.toLowerCase().includes(search.toLowerCase())).map((u) => (
                <div key={u.id} onClick={() => pickUser(u)}
                  style={{ display: 'flex', gap: 8, padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selUser?.id === u.id ? '#eff6ff' : '#fff', fontSize: 13 }}>
                  <b style={{ width: 56 }}>{u.extension_number || '—'}</b>
                  <span style={{ flex: 1 }}>{uName(u)}</span>
                  {u.has_phone && <span title="Has a desk phone">☎</span>}
                  {u.count > 0 && <span style={{ color: '#0b5cff' }}>{u.count}</span>}
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {!selUser && <div style={{ fontSize: 13, color: '#6b7280' }}>Choose a user to edit their own speed dials.</div>}
            {selUser && (
              <>
                <h4 style={{ margin: '0 0 4px' }}>{uName(selUser)} {selUser.extension_number ? `(${selUser.extension_number})` : ''}</h4>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
                  {selUser.has_phone ? 'Has a desk phone.' : 'No desk phone assigned yet — these will show once one is.'} Shown after the {orgItems.length} organisation speed dial{orgItems.length === 1 ? '' : 's'}; room for {room} more on the phone.
                </p>
                <ListEditor items={userItems} onChange={setUserItems} max={max} />
                {userItems.length > room && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Only the first {room} of these fit on the phone.</div>}
              </>
            )}
          </div>
        </div>
      )}

      {(tab === 'org' || selUser) && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }}>
          <ZoomBtn variant="primary" onClick={() => { if (!busy) save(); }}>{busy ? 'Saving…' : 'Save'}</ZoomBtn>
          <ZoomBtn onClick={() => { if (!busy) discard(); }}>Discard changes</ZoomBtn>
          {msg && <span style={{ fontSize: 13, color: msg === 'Saved.' ? '#15803d' : '#b91c1c' }}>{msg}</span>}
        </div>
      )}
      {tab === 'user' && !selUser && msg && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 10 }}>{msg}</div>}
    </div>
  );
}
